import { createContext } from 'react';

import type { STORAGE_KEYS, STORAGE_VALUES } from 'types/storage';

type StorageContextType = {
  getItem: <T extends STORAGE_KEYS>(key: T) => STORAGE_VALUES[T] | null;
  setItem: <T extends STORAGE_KEYS>(key: T, value: STORAGE_VALUES[T]) => void;
  clearItem: (key: STORAGE_KEYS) => void;
  clearEntireStorage: () => void;
};

const StorageContext = createContext<StorageContextType>({
  getItem: () => null,
  setItem: () => {},
  clearItem: () => {},
  clearEntireStorage: () => {},
});

interface StorageProviderProps {
  children: React.ReactNode;
}

const StorageProvider = ({ children }: StorageProviderProps) => {
  const getItem = <T extends STORAGE_KEYS>(key: T) => {
    const item = localStorage.getItem(key);

    if (!item) {
      return null;
    }

    return JSON.parse(item);
  };

  const setItem = <T extends STORAGE_KEYS>(key: T, value: STORAGE_VALUES[T]) => {
    localStorage.setItem(key, JSON.stringify(value));
  };

  const clearItem = (key: STORAGE_KEYS) => {
    localStorage.removeItem(key);
  };

  const clearEntireStorage = () => {
    localStorage.clear();
    window.location.reload();
  };

  return (
    <StorageContext.Provider
      value={{
        getItem,
        setItem,
        clearItem,
        clearEntireStorage,
      }}>
      {children}
    </StorageContext.Provider>
  );
};

export { StorageProvider, StorageContext };
